import { useCallback, useEffect, useMemo, useState } from 'react';
import type { RegularBreakpoints } from '@mui/material/Grid';
import pick from 'lodash/pick';
import { STORAGE } from '#/config/consts';
import useSession from '#/common/hooks/useSession';
import storage from '#/common/utils/storage';
import { UserRole } from '#/common/types/userTypes';

const store = storage(localStorage);

export type Item = {
  key: string;
  title: string;
  icon: string;
  subtitle?: string;
  show: boolean;
  colSpan?: RegularBreakpoints;
};

export type SavedItem = Pick<Item, 'key' | 'show' | 'colSpan'>;

const getKey = (user: string | undefined) => `${user ?? '?'}/${STORAGE.DASHBOARD}`;

const shouldShowMyView = (role: string | null | undefined, tenants: any[] | undefined): boolean => {
  if (!role) return false;

  if (tenants && tenants.length > 0) {
    const hasElevatedRole = tenants.some(
      (t) => t.role === UserRole.trane_admin || t.role === UserRole.asset_manager,
    );

    if (hasElevatedRole) return false;
  }

  return role === UserRole.technician;
};

const getDefaultItems = (role: string | null | undefined, tenants: any[] | undefined): Item[] => {
  const showMyView = shouldShowMyView(role, tenants);
  const showAllView = !showMyView;

  return [
    // All Work Orders widgets (available for everyone)
    {
      key: 'work-orders-per-state',
      title: 'All Work Orders by Status',
      icon: 'grid-2',
      show: showAllView,
      colSpan: { ...defaultColSpan },
    },
    {
      key: 'work-orders-per-priority',
      title: 'All Work Orders by Priority',
      icon: 'circle-notch',
      show: showAllView,
      colSpan: { ...defaultColSpan },
    },
    {
      key: 'work-orders-per-category',
      title: 'All Work Orders by Category',
      icon: 'chart-pie-simple',
      show: showAllView,
      colSpan: { ...defaultColSpan },
    },
    {
      key: 'work-orders-per-due-date',
      title: 'All Pending Work Orders by Due Date',
      subtitle: 'Work Orders with Due Date in the Next 7 Days',
      icon: 'chart-simple',
      show: showAllView,
      colSpan: { ...defaultColSpan },
    },
    // My Work Orders widgets (available for everyone)
    {
      key: 'my-work-orders-per-state',
      title: 'My Work Orders by Status',
      icon: 'grid-2',
      show: showMyView,
      colSpan: { ...defaultColSpan },
    },
    {
      key: 'my-work-orders-per-priority',
      title: 'My Work Orders by Priority',
      icon: 'circle-notch',
      show: showMyView,
      colSpan: { ...defaultColSpan },
    },
    {
      key: 'my-work-orders-per-category',
      title: 'My Work Orders by Category',
      icon: 'chart-pie-simple',
      show: showMyView,
      colSpan: { ...defaultColSpan },
    },
    {
      key: 'my-work-orders-per-due-date',
      title: 'My Pending Work Orders by Due Date',
      subtitle: 'Work Orders with Due Date in the Next 7 Days',
      icon: 'chart-simple',
      show: showMyView,
      colSpan: { ...defaultColSpan },
    },
    {
      key: 'assets-per-condition',
      title: 'Assets by Condition',
      icon: 'chart-pie-simple',
      show: true,
      colSpan: { ...defaultColSpan },
    },
    {
      key: 'assets-per-location',
      title: 'Top 10 Locations by Number of Assets',
      icon: 'circle-notch',
      show: true,
      colSpan: { ...defaultColSpan },
    },
    {
      key: 'assets-per-expenses',
      title: 'Most Costly Asset Models',
      subtitle: 'Most Expensive Assets for Maintenance',
      icon: 'chart-simple',
      show: true,
      colSpan: { md: 12, xl: 12 },
    },
    {
      key: 'assets-per-type',
      title: 'Top 10 Asset Types',
      subtitle: 'Number of Assets Grouped by Type',
      icon: 'chart-pie-simple',
      show: false,
      colSpan: { ...defaultColSpan },
    },
    {
      key: 'findings-per-type',
      title: 'Findings by Type',
      icon: 'grid-2',
      show: true,
      colSpan: { ...defaultColSpan },
    },
    {
      key: 'findings-per-priority',
      title: 'Findings by Priority',
      icon: 'circle-notch',
      show: true,
      colSpan: { ...defaultColSpan },
    },
    {
      key: 'findings-per-asset-model',
      title: 'Top 10 Asset Models with Most Findings',
      icon: 'chart-simple',
      show: true,
      colSpan: { md: 12, xl: 12 },
    },
    {
      key: 'work-orders-sla-data',
      title: 'SLA Adherence',
      subtitle: 'Data from Active Work Orders',
      icon: 'chart-simple',
      show: true,
      colSpan: { md: 12, xl: 12 },
    },
    {
      key: 'data-discrepancies',
      title: 'FSM Data Discrepancies',
      icon: 'grid-2',
      show: true,
      colSpan: { ...defaultColSpan },
    },
  ] satisfies Item[];
};

const getItems = (
  userId: string | undefined,
  role: string | null | undefined,
  tenants: any[] | undefined,
): Item[] => {
  if (!userId) {
    return [];
  }
  const key = getKey(userId);
  const savedItems = store.get<SavedItem[]>(key);

  const defaultItems = getDefaultItems(role, tenants);

  if (!savedItems) {
    return defaultItems;
  }

  const items = savedItems
    .map((item) => {
      const defItem = defaultItems.find((di) => di.key === item.key);
      if (!defItem) return null;

      return {
        ...defItem,
        colSpan: item.colSpan || defItem.colSpan,
        show: item.show ?? defItem.show,
        title: defItem.title,
        subtitle: defItem.subtitle,
      };
    })
    .filter(Boolean) as Item[];

  return items;
};

const defaultColSpan = {
  xs: 12,
  md: 6,
  xl: 6,
};

const storeItems = (user: string | undefined, items: Item[]) => {
  const key = getKey(user);
  const toSave = items.map((item) => pick(item, 'key', 'show', 'colSpan'));
  store.set(key, toSave);
};

export type ItemKeys =
  | 'work-orders-per-state'
  | 'work-orders-per-priority'
  | 'work-orders-per-category'
  | 'work-orders-per-due-date'
  | 'my-work-orders-per-state'
  | 'my-work-orders-per-priority'
  | 'my-work-orders-per-category'
  | 'my-work-orders-per-due-date'
  | 'assets-per-condition'
  | 'assets-per-location'
  | 'assets-per-expenses'
  | 'assets-per-type'
  | 'findings-per-type'
  | 'findings-per-priority'
  | 'findings-per-asset-model'
  | 'work-orders-sla-data'
  | 'data-discrepancies';

const useDashboardItems = () => {
  const session = useSession();
  const storedItems = getItems(session.user?.id, session.user?.role, session.tenants);
  const [items, setItems] = useState<Item[]>(storedItems);

  // Get the default items based on user role
  const defaultItems = useMemo(
    () => getDefaultItems(session.user?.role, session.tenants),
    [session.user?.role, session.tenants],
  );

  // Generate JSON string for comparison
  const defaultItemsJson = useMemo(() => JSON.stringify(defaultItems), [defaultItems]);

  useEffect(() => {
    const storedItems = getItems(session.user?.id, session.user?.role, session.tenants);
    setItems((oldItems) => {
      const oldJson = JSON.stringify(oldItems);
      const newJson = JSON.stringify(storedItems);
      return oldJson === newJson ? oldItems : storedItems;
    });
  }, [session.user?.id, session.user?.role, session.tenants]);

  const saveItems = useCallback(
    (newItems: Item[]) => {
      storeItems(session.user?.id, newItems);
      setItems(newItems);
    },
    [session.user?.id],
  );

  return { items, saveItems, defaultItems, defaultItemsJson };
};

export default useDashboardItems;
